import { isBlobNode } from "@ide/shared";
import type { GitRemoteResult } from "@ide/shared/desktop";
import type { ProjectSession } from "@/collab/session";
import { desktop } from "@/desktop/bridge";
import { deleteFile, writeFile } from "./file-ops";

/**
 * Extensão Git (ADR 0011). O projeto vive no servidor do Kompil (Yjs); o Git conversa com o GitHub
 * pela API REST (blobs, árvores, commits e refs), pelo app desktop, com o token de cada pessoa.
 * Repositórios de outros servidores (GitLab, Bitbucket, Azure DevOps, Gitea…) vão por HTTPS com o
 * Git embutido no app (ADR 0019): o `repo` é o endereço https em vez de "dono/nome".
 * A "base" (commit e árvore da última sincronização) é guardada no servidor, comum ao time.
 */
export interface GitLink {
  /** "dono/nome" (GitHub pela API) ou https://servidor/caminho (Git por HTTPS). */
  repo: string;
  branch: string;
  baseCommit: string;
  baseFiles: Record<string, string>;
  updatedAt: string;
  updatedByHandle: string | null;
}

export type ChangeKind = "M" | "A" | "D";
export interface Change {
  path: string;
  kind: ChangeKind;
}

export const GIT_LIMITS = { maxFiles: 1_000, maxFileBytes: 1_000_000, maxTotalBytes: 20_000_000 };

export class GitError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

const encoder = new TextEncoder();

/** SHA-1 de blob do Git: "blob <bytes>\0<conteúdo>" — o mesmo que `git hash-object`. */
export async function blobSha(text: string): Promise<string> {
  const body = encoder.encode(text);
  const header = encoder.encode(`blob ${body.length}\0`);
  const all = new Uint8Array(header.length + body.length);
  all.set(header);
  all.set(body, header.length);
  const digest = await crypto.subtle.digest("SHA-1", all);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Alterações do projeto em relação à base (M = modificado, A = novo, D = apagado). */
export function computeChanges(local: Record<string, string>, base: Record<string, string>): Change[] {
  const changes: Change[] = [];
  // Arquivos binários do projeto não têm texto (não entram em `local`): não contam como apagados.
  for (const [path, sha] of Object.entries(local)) {
    if (!(path in base)) changes.push({ path, kind: "A" });
    else if (base[path] !== sha) changes.push({ path, kind: "M" });
  }
  for (const path of Object.keys(base))
    if (!(path in local) && !projectBlobPaths.has(path)) changes.push({ path, kind: "D" });
  return changes.sort((a, b) => a.path.localeCompare(b.path));
}

/** Conteúdo de um blob do GitHub (base64) como texto; null se for binário. */
export function decodeBlob(base64: string): string | null {
  const bytes = Uint8Array.from(atob(base64.replace(/\n/g, "")), (c) => c.charCodeAt(0));
  if (bytes.includes(0)) return null;
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

// ───────────── Qualquer servidor Git (pelo app desktop) ─────────────

/** O vínculo é com um servidor Git por HTTPS (não pela API do GitHub). */
export const isRemoteUrl = (repo: string) => repo.includes("://");

/** Nome do servidor para a interface: "GitHub", "gitlab.com"… */
export function remoteName(repo: string): string {
  if (!isRemoteUrl(repo)) return "GitHub";
  const host = new URL(repo).hostname;
  return host === "github.com" ? "GitHub" : host;
}

/** Rótulo curto do repositório: "dono/nome" ou "servidor/caminho". */
export function repoLabel(repo: string): string {
  if (!isRemoteUrl(repo)) return repo;
  const u = new URL(repo);
  return `${u.host}${u.pathname.replace(/\.git$/, "")}`;
}

/** Página da branch no site do servidor. */
export function repoWebUrl(repo: string, branch: string): string {
  if (!isRemoteUrl(repo)) return `https://github.com/${repo}/tree/${branch}`;
  const base = repo.replace(/\.git$/, "");
  const host = new URL(repo).hostname;
  if (host === "github.com") return `${base}/tree/${branch}`;
  if (host === "bitbucket.org") return `${base}/src/${branch}`;
  if (host.includes("gitlab")) return `${base}/-/tree/${branch}`;
  return base;
}

function remoteApi() {
  if (!desktop?.gitRemote)
    throw new GitError(0, "Atualize o Kompil Desktop para usar Git com outros servidores.");
  return desktop.gitRemote;
}

async function remote<T>(call: Promise<GitRemoteResult<T>>): Promise<T> {
  const res = await call;
  if (!res.ok) throw new GitError(res.status, res.error);
  return res.data;
}

/**
 * Repositório digitado → "dono/nome" do GitHub (quando a conta do GitHub está conectada) ou o
 * endereço https normalizado pelo app (qualquer servidor, git@host:caminho também vale).
 */
export async function resolveRepoInput(input: string, githubConnected: boolean): Promise<string | null> {
  const gh = parseRepo(input);
  if (gh && githubConnected) return gh;
  const text = input.trim();
  const url = gh && !/[:/]{2}|@/.test(text) ? `https://github.com/${gh}.git` : text;
  if (!desktop?.gitRemote) return gh;
  return desktop.gitRemote.normalize(url);
}

// ───────────── API do GitHub (pelo app desktop) ─────────────

async function gh<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (!desktop) throw new GitError(0, "O Git funciona no Kompil Desktop.");
  const res = await desktop.git.api<T & { message?: string }>(method, path, body);
  if (res.status === 0) throw new GitError(0, "Sem conexão com o GitHub.");
  if (res.status === 401) throw new GitError(401, "O GitHub recusou o token. Conecte a conta de novo.");
  if (res.status >= 400) {
    const message = (res.data as { message?: string } | null)?.message ?? `Erro ${res.status}`;
    throw new GitError(
      res.status,
      res.status === 404 ? "Repositório ou branch não encontrado (ou sem acesso)." : message,
    );
  }
  return res.data;
}

const enc = encodeURIComponent;
const refPath = (repo: string, branch: string) =>
  `/repos/${repo}/git/ref/heads/${branch.split("/").map(enc).join("/")}`;

/** Último commit da branch (null se a branch não existe). */
export async function headCommit(repo: string, branch: string): Promise<string | null> {
  if (isRemoteUrl(repo)) return remote(remoteApi().head(repo, branch));
  try {
    const ref = await gh<{ object: { sha: string } }>("GET", refPath(repo, branch));
    return ref.object.sha;
  } catch (err) {
    if (err instanceof GitError && err.status === 404) return null;
    throw err;
  }
}

/** Arquivos (blobs) de um commit: caminho → SHA e tamanho. */
export async function commitFiles(repo: string, commit: string, branch: string) {
  if (isRemoteUrl(repo)) return remote(remoteApi().files(repo, branch, commit));
  const c = await gh<{ tree: { sha: string } }>("GET", `/repos/${repo}/git/commits/${commit}`);
  const tree = await gh<{
    tree: Array<{ path: string; type: string; sha: string; size?: number }>;
    truncated: boolean;
  }>("GET", `/repos/${repo}/git/trees/${c.tree.sha}?recursive=1`);
  const files: Record<string, { sha: string; size: number }> = {};
  for (const e of tree.tree) if (e.type === "blob") files[e.path] = { sha: e.sha, size: e.size ?? 0 };
  return { tree: c.tree.sha, files, truncated: tree.truncated };
}

export async function blobText(repo: string, sha: string, branch: string): Promise<string | null> {
  if (isRemoteUrl(repo)) return remote(remoteApi().blob(repo, branch, sha));
  const blob = await gh<{ content: string; encoding: string }>("GET", `/repos/${repo}/git/blobs/${sha}`);
  return blob.encoding === "base64" ? decodeBlob(blob.content) : blob.content;
}

// ───────────── Arquivos do projeto ─────────────

/** Texto atual de todos os arquivos do projeto (abre os documentos que ainda não estão abertos). */
/** Caminhos dos arquivos binários na última leitura do projeto (ver `computeChanges`). */
const projectBlobPaths = new Set<string>();

/** Texto de cada arquivo do projeto (binários ficam de fora: não têm texto). */
export async function readProjectTexts(session: ProjectSession): Promise<Record<string, string>> {
  const tree = session.tree();
  projectBlobPaths.clear();
  for (const n of tree.nodes.values())
    if (isBlobNode(n) && tree.paths.has(n.id)) projectBlobPaths.add(tree.paths.get(n.id)!);
  const files = [...tree.nodes.values()].filter(
    (n) => n.type === "file" && tree.paths.has(n.id) && !isBlobNode(n),
  );
  const out: Record<string, string> = {};
  const queue = [...files];
  const worker = async () => {
    for (let node = queue.shift(); node; node = queue.shift()) {
      const handle = session.openFile(node.id);
      try {
        await session.loaded(handle);
        out[tree.paths.get(node.id)!] = handle.text.toString();
      } finally {
        handle.release();
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(8, files.length) }, worker));
  return out;
}

export async function shasOf(texts: Record<string, string>): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  await Promise.all(Object.entries(texts).map(async ([p, t]) => (out[p] = await blobSha(t))));
  return out;
}

// ───────────── Operações ─────────────

/**
 * Commit e push na branch (só avança: nunca reescreve o histórico). Com `only`, entram só esses
 * caminhos (as alterações preparadas); o resto continua como alteração pendente.
 */
export async function commitAndPush(
  link: GitLink,
  texts: Record<string, string>,
  message: string,
  only?: ReadonlySet<string>,
  author?: { name: string; email: string },
) {
  if (isRemoteUrl(link.repo)) return commitAndPushRemote(link, texts, message, only, author);
  const head = await headCommit(link.repo, link.branch);
  if (head && head !== link.baseCommit) {
    throw new GitError(409, "O GitHub tem commits novos nesta branch. Sincronize (pull) antes do push.");
  }
  const shas = await shasOf(texts);
  const all = computeChanges(shas, link.baseFiles);
  const changes = only && only.size ? all.filter((c) => only.has(c.path)) : all;
  if (!changes.length) throw new GitError(400, "Nenhuma alteração para commitar.");
  const parent = head ?? link.baseCommit;
  const base = await gh<{ tree: { sha: string } }>("GET", `/repos/${link.repo}/git/commits/${parent}`);
  const tree = await gh<{ sha: string }>("POST", `/repos/${link.repo}/git/trees`, {
    base_tree: base.tree.sha,
    tree: changes.map((c) =>
      c.kind === "D"
        ? { path: c.path, mode: "100644", type: "blob", sha: null }
        : { path: c.path, mode: "100644", type: "blob", content: texts[c.path] },
    ),
  });
  const commit = await gh<{ sha: string }>("POST", `/repos/${link.repo}/git/commits`, {
    message,
    tree: tree.sha,
    parents: [parent],
    // Autoria por pessoa (quem escreveu); quem envia (o dono do token) fica como committer.
    ...(author ? { author: { ...author, date: new Date().toISOString() } } : {}),
  });
  await gh("PATCH", `/repos/${link.repo}/git/refs/heads/${link.branch.split("/").map(enc).join("/")}`, {
    sha: commit.sha,
    force: false,
  });
  const baseFiles = { ...link.baseFiles };
  for (const c of changes) {
    if (c.kind === "D") delete baseFiles[c.path];
    else baseFiles[c.path] = shas[c.path]!;
  }
  return { commit: commit.sha, baseFiles, changes };
}

/** Commit e push num servidor Git por HTTPS (o app monta a árvore e envia; só avança). */
async function commitAndPushRemote(
  link: GitLink,
  texts: Record<string, string>,
  message: string,
  only: ReadonlySet<string> | undefined,
  author: { name: string; email: string } | undefined,
) {
  const shas = await shasOf(texts);
  const all = computeChanges(shas, link.baseFiles);
  const changes = only && only.size ? all.filter((c) => only.has(c.path)) : all;
  if (!changes.length) throw new GitError(400, "Nenhuma alteração para commitar.");
  const commit = await remote(
    remoteApi().commitPush({
      url: link.repo,
      branch: link.branch,
      parent: link.baseCommit,
      changes: changes.map((c) => ({ path: c.path, content: c.kind === "D" ? null : texts[c.path]! })),
      message,
      author: author ?? { name: "Kompil", email: "kompil@users.noreply.rrmind.tech" },
    }),
  );
  const baseFiles = { ...link.baseFiles };
  for (const c of changes) {
    if (c.kind === "D") delete baseFiles[c.path];
    else baseFiles[c.path] = shas[c.path]!;
  }
  return { commit, baseFiles, changes };
}

/**
 * Traz os commits novos do GitHub. Arquivo que só mudou lá é atualizado aqui; arquivo que mudou
 * dos dois lados vira conflito e fica com a versão do Kompil (o próximo commit a envia).
 */
export async function pull(link: GitLink, session: ProjectSession, texts: Record<string, string>) {
  const head = await headCommit(link.repo, link.branch);
  if (!head) throw new GitError(404, `A branch ${link.branch} não existe mais no GitHub.`);
  if (head === link.baseCommit)
    return { commit: head, baseFiles: link.baseFiles, applied: 0, conflicts: [] as string[] };
  const remote = await commitFiles(link.repo, head, link.branch);
  const local = await shasOf(texts);
  const baseFiles: Record<string, string> = {};
  const conflicts: string[] = [];
  let applied = 0;
  const paths = new Set([...Object.keys(remote.files), ...Object.keys(link.baseFiles)]);
  for (const path of paths) {
    const r = remote.files[path];
    const b = link.baseFiles[path];
    const l = local[path];
    if (r && r.size > GIT_LIMITS.maxFileBytes) continue;
    if (r?.sha === b) {
      if (b) baseFiles[path] = b;
      continue; // não mudou no GitHub
    }
    const changedHere = l !== b;
    if (changedHere && l !== r?.sha) {
      conflicts.push(path);
      if (r) baseFiles[path] = r.sha;
      continue;
    }
    if (!r) {
      deleteFile(session, path);
      applied++;
      continue;
    }
    const text = await blobText(link.repo, r.sha, link.branch);
    if (text === null) continue; // binário: fica só no servidor
    await writeFile(session, path, text);
    baseFiles[path] = r.sha;
    applied++;
  }
  return { commit: head, baseFiles, applied, conflicts };
}

export interface CommitInfo {
  sha: string;
  message: string;
  author: string;
  login: string | null;
  date: string;
  url: string;
}

/** Últimos commits da branch no GitHub (mais novos primeiro). */
export async function commitHistory(repo: string, branch: string, limit = 20): Promise<CommitInfo[]> {
  if (isRemoteUrl(repo)) return remote(remoteApi().log(repo, branch, limit));
  const list = await gh<
    Array<{
      sha: string;
      html_url: string;
      commit: { message: string; author: { name: string; date: string } | null };
      author: { login: string } | null;
    }>
  >("GET", `/repos/${repo}/commits?sha=${enc(branch)}&per_page=${Math.min(100, limit)}`);
  return list.map((c) => ({
    sha: c.sha,
    message: c.commit.message,
    author: c.commit.author?.name ?? c.author?.login ?? "?",
    login: c.author?.login ?? null,
    date: c.commit.author?.date ?? "",
    url: c.html_url,
  }));
}

/** Situação da base do Kompil diante da branch no GitHub. */
export interface RemoteStatus {
  /** Commits no GitHub que o Kompil ainda não trouxe (pull). */
  behind: number;
  /** A branch foi reescrita (force push) e a base do Kompil não está mais no histórico. */
  diverged: boolean;
}

export async function remoteStatus(link: GitLink): Promise<RemoteStatus> {
  if (isRemoteUrl(link.repo)) return remote(remoteApi().status(link.repo, link.branch, link.baseCommit));
  const res = await gh<{ status: string; ahead_by: number; behind_by: number }>(
    "GET",
    `/repos/${link.repo}/compare/${link.baseCommit}...${link.branch.split("/").map(enc).join("/")}`,
  );
  // "ahead" aqui é a branch à frente da base: commits novos no GitHub.
  return { behind: res.ahead_by, diverged: res.status === "diverged" };
}

/** Copia a branch inteira para o projeto (importar): sobrescreve e apaga o que não está lá. */
export async function importInto(
  session: ProjectSession,
  repo: string,
  branch: string,
  onProgress?: (n: number, total: number) => void,
) {
  const head = await headCommit(repo, branch);
  if (!head) throw new GitError(404, `A branch ${branch} não existe em ${repoLabel(repo)}.`);
  const remote = await commitFiles(repo, head, branch);
  const entries = Object.entries(remote.files).filter(([, f]) => f.size <= GIT_LIMITS.maxFileBytes);
  if (entries.length > GIT_LIMITS.maxFiles) {
    throw new GitError(
      413,
      `O repositório tem ${entries.length} arquivos; o limite para importar é ${GIT_LIMITS.maxFiles}.`,
    );
  }
  const baseFiles: Record<string, string> = {};
  let total = 0;
  let done = 0;
  for (const [path, f] of entries) {
    const text = await blobText(repo, f.sha, branch);
    done++;
    onProgress?.(done, entries.length);
    if (text === null) continue;
    total += text.length;
    if (total > GIT_LIMITS.maxTotalBytes)
      throw new GitError(413, "O repositório é grande demais para importar.");
    await writeFile(session, path, text);
    baseFiles[path] = f.sha;
  }
  const tree = session.tree();
  for (const [id, path] of tree.paths) {
    if (tree.nodes.get(id)?.type === "file" && !(path in remote.files)) deleteFile(session, path);
  }
  return { commit: head, baseFiles };
}

/** Cria o repositório no GitHub (com um commit inicial) para publicar o projeto. */
export async function createRepo(name: string, privateRepo: boolean, description?: string) {
  const repo = await gh<{ full_name: string; default_branch: string }>("POST", "/user/repos", {
    name,
    private: privateRepo,
    description: description?.slice(0, 300),
    auto_init: true,
  });
  // O GitHub cria o commit inicial de forma assíncrona: espera a branch aparecer.
  for (let i = 0; i < 10; i++) {
    const head = await headCommit(repo.full_name, repo.default_branch);
    if (head) {
      const files = await commitFiles(repo.full_name, head, repo.default_branch);
      return {
        repo: repo.full_name,
        branch: repo.default_branch,
        baseCommit: head,
        baseFiles: Object.fromEntries(Object.entries(files.files).map(([p, f]) => [p, f.sha])),
      };
    }
    await new Promise((r) => setTimeout(r, 800));
  }
  throw new GitError(504, "O GitHub não terminou de criar o repositório. Tente de novo em instantes.");
}

/** "dono/repo" a partir de URL do GitHub ou do texto digitado. */
export function parseRepo(input: string): string | null {
  const m =
    /^(?:https?:\/\/github\.com\/|git@github\.com:)?([A-Za-z0-9_.-]{1,100})\/([A-Za-z0-9_.-]{1,100}?)(?:\.git)?\/?$/.exec(
      input.trim(),
    );
  return m ? `${m[1]}/${m[2]}` : null;
}
